import { DateTime } from 'luxon';
import { WithSnackbarProps } from 'notistack';
import { intersection, without } from 'ramda';
import React, {
  useEffect,
  useState
} from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router';

import {
  CircularProgress,
  colors,
  Container,
  Fab,
  Hidden,
  LabelDisplayedRowsArgs,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Theme,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import { Add as IconAdd } from '@material-ui/icons';

import { SplashScreen } from '@checkit/react-components';
import { NoCheckpointTypeIcon } from '@checkit/react-components/build/components/Icons';

import { loaderReducer } from '../../common/delayedLoader';
import { FloatButton } from '../../common/FloatButton';
import TablePaginationActions from '../../common/TablePaginationActions';
import MaterialUiLink from '../../components/MaterialUiLink';
import { useAxiosDispatch } from '../../middleware/axios';
import { createSnackbar } from '../../notifications/actions';
import { setRowsPerPage } from '../../preferences/actions';
import { Preferences } from '../../preferences/state';
import { State } from '../../state';
import { appMessages } from '../../translations';
import {
  clearRecentlyModified,
  deleteCheckpointType,
  getCheckpointTypeCount,
  getCheckpointTypes,
  isDeleteCheckpointTypeSuccess
} from '../actions';
import { CheckpointTypes } from '../state';

import { CheckpointTypeActions } from './CheckpointTypeActions';
import { messages } from './CheckpointTypeList.translations';

export interface Props extends RouteComponentProps, WithSnackbarProps {
  checkpointTypes: CheckpointTypes;
  preferences: Preferences;
}

const formatDate: (d: DateTime) => string = d => d.toLocaleString(DateTime.DATETIME_MED);

const useStyles = makeStyles((theme: Theme) => ({
  highlight: {
    backgroundColor: colors.yellow['200']
  },
  spacer: {
    [theme.breakpoints.down('sm')]: {
      display: 'none'
    }
  },
  tableCell: {
    whiteSpace: 'pre-wrap'
  },
  tableRoot: {
    borderColor: theme.palette.divider,
    borderTop: 'solid 1px'
  },
  toolbar: {
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center'
    }
  }
}));

export const CheckpointTypeList: React.FC<Props> = (props: Props) => {
  const {
    checkpointTypes,
    preferences,
    location: routeLocation
  } = props;

  const { formatMessage } = useIntl();
  const dispatch = useAxiosDispatch( );

  const [page, setPage] = useState(0);

  const classes = useStyles();

  const theme = useTheme();
  const showRowsPerPage = useMediaQuery(theme.breakpoints.up('md'));

  const initialState = { loading: true, displayLoader: checkpointTypes.count === null };
  const [loadingState, loadingDispatch] = React.useReducer(loaderReducer, initialState);

  const rowsPerPage = preferences.rowsPerPage;

  const displayedRows = ({ from, to, count }: LabelDisplayedRowsArgs) =>
    formatMessage(messages['checkpointTypes.list.displayedRows'], { from, to, count });

  // when leaving the list reset the checkpoint types recently modified
  useEffect(() => {
    return () => {
      dispatch(clearRecentlyModified());
    };
  }, [dispatch]);

  useEffect(() => {
    if (checkpointTypes.count === null) {
      dispatch(getCheckpointTypeCount());
    }
  }, [dispatch, checkpointTypes.count]);

  useEffect(() => {
    if (checkpointTypes.count !== null && checkpointTypes.count > 0) {
      if (page >= Math.ceil(checkpointTypes.count / rowsPerPage)) {
        // We're off the end of the page range, decrease page (which will run the effect again)
        setPage(page - 1);
      } else {
        loadingDispatch('loading');

        // Delay before indicating loading
        window.setTimeout(() => loadingDispatch('display'), 500);
        dispatch(getCheckpointTypes({
          skip: page * rowsPerPage,
          take: rowsPerPage
        }));
      }
    }
  }, [dispatch, checkpointTypes.count, page, rowsPerPage]);

  useEffect(() => {
    if (checkpointTypes.count !== null) {
      loadingDispatch('loaded');
    }
  }, [checkpointTypes]);

  function handleChangePage(
    _: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) {
    dispatch(clearRecentlyModified());
    setPage(newPage);
  }

  function handleChangeRowsPerPage(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    dispatch(setRowsPerPage(parseInt(event.target.value, 10)));
    setPage(0);
  }

  if (checkpointTypes.count === 0 && !loadingState.displayLoader) {
    return (
      <SplashScreen
        icon={NoCheckpointTypeIcon}
        heading={formatMessage(messages['checkpointTypes.list.empty.primary'])}
        details={formatMessage(messages['checkpointTypes.list.empty.secondary'])}
      >
        <Container style={{ textAlign: 'center' }}>
          <Fab
            color="primary"
            variant="extended"
            style={{ marginTop: '1em' }}
            component={MaterialUiLink}
            to={{ pathname: '/checkpointtypes/new', state: { previousLocation: routeLocation } }}
          >
            <IconAdd style={{ marginRight: '0.3em' }} />
            <span>{formatMessage(messages['checkpointTypes.list.empty.action'])}</span>
          </Fab>
        </Container>
      </SplashScreen>
    );
  }

  return (
    <>
      <Paper>
        <CheckpointTypeTable/>
      </Paper>
      <FloatButton
        label={formatMessage(appMessages['app.add'])}
        innerText={formatMessage(appMessages['app.add'])}
        to={{pathname: '/checkpointtypes/new', state: {previousLocation: routeLocation}}}
      />
    </>
  );

  function CheckpointTypeTable() {
    const recentlyModifiedOverlapWithAllCodes = checkpointTypes.allCodes.filter(code =>
      checkpointTypes.recentlyModified.indexOf(code) > -1);

    const pageCheckpointTypes = without(recentlyModifiedOverlapWithAllCodes, checkpointTypes.allCodes);

    // If a document that was recently modified appears on the page, don't count it twice
    const actualRowsPerPage = rowsPerPage +
      checkpointTypes.recentlyModified.length -
      intersection(checkpointTypes.allCodes, checkpointTypes.recentlyModified).length;

    function CheckpointTypeTableBody() {
      return (
        <TableBody>
          {loadingState.displayLoader ? (
            <TableRow>
              <TableCell align="center" colSpan={5}>
                <CircularProgress color="inherit" />
              </TableCell>
            </TableRow>
          ) : [
              ...checkpointTypes.recentlyModified.map(
                code => (<CheckpointTypeRow key={code} code={code} highlight={true} />)
              ),
              ...pageCheckpointTypes.map(code => (
                <CheckpointTypeRow key={code} code={code} />
              ))]
          }
        </TableBody>
      );
    }

    return (
      <Table classes={{ root: classes.tableRoot }}>
        <TableHead>
          <TableRow>
            <TableCell>{formatMessage(messages['checkpointTypes.list.name'])}</TableCell>
            <Hidden mdDown={true}>
              <TableCell>
                {formatMessage(messages['checkpointTypes.list.description'])}
              </TableCell>
              <TableCell>{formatMessage(messages['checkpointTypes.list.created'])}</TableCell>
              <TableCell>{formatMessage(messages['checkpointTypes.list.updated'])}</TableCell>
            </Hidden>
            <TableCell>{formatMessage(messages['checkpointTypes.list.actions'])}</TableCell>
          </TableRow>
        </TableHead>
        <CheckpointTypeTableBody />
        <TableFooter>
          <TableRow>
            <TablePagination
              classes={{ spacer: classes.spacer, toolbar: classes.toolbar }}
              rowsPerPageOptions={showRowsPerPage ? [5, 10, 20] : []}
              count={checkpointTypes.count || 0}
              rowsPerPage={actualRowsPerPage}
              page={page}
              SelectProps={{
                inputProps: {
                  'aria-label': formatMessage(messages['checkpointTypes.list.rowsPerPage'])
                },
                native: true
              }}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
              labelRowsPerPage={formatMessage(messages['checkpointTypes.list.rowsPerPage'])}
              labelDisplayedRows={displayedRows}
            />
          </TableRow>
        </TableFooter>
      </Table>
    );
  }

  function CheckpointTypeRow({ code, highlight }: { code: string, highlight?: boolean }) {
    const checkpointType = checkpointTypes.byCode[code];
    if (checkpointType == null) {
      throw new Error(`Checkpoint type row code missing from state: "${code}"`);
    }

    function handleDeleteCheckpointType(): Promise<void> {
      return dispatch(deleteCheckpointType(code))
        .then((action) => {
          if (isDeleteCheckpointTypeSuccess(action)) {
            dispatch(createSnackbar({
              message: formatMessage(messages['checkpointTypes.list.delete.success']),
              options: {
                variant: 'success'
              }
            }));
          } else {
            const errorType = action ? (action as any).error.response.data.message : null;
            if (errorType === messages.CHECKPOINT_TYPE_ASSIGNED_WITH_WORKS.id) {
              dispatch(createSnackbar({
                message: formatMessage(messages.CHECKPOINT_TYPE_ASSIGNED_WITH_WORKS),
                options: {
                  anchorOrigin: {
                    horizontal: 'center',
                    vertical: 'top'
                  },
                  autoHideDuration: 10000,
                  variant: 'error'
                }
              }));
            } else if (errorType === messages.CHECKPOINT_TYPE_HAS_CHECKPOINTS.id) {
              dispatch(createSnackbar({
                message: formatMessage(messages.CHECKPOINT_TYPE_HAS_CHECKPOINTS),
                options: {
                  anchorOrigin: {
                    horizontal: 'center',
                    vertical: 'top'
                  },
                  autoHideDuration: 10000,
                  variant: 'error'
                }
              }));
            } else {
              dispatch(createSnackbar({
                message: formatMessage(messages['checkpointTypes.list.delete.failure']),
                options: {
                  variant: 'error'
                }
              }));
            }
          }
        });
    }
    return (
      <TableRow key={code} className={highlight ? classes.highlight : ''}>
        <TableCell className={classes.tableCell}>{checkpointType.name}</TableCell>
        <Hidden mdDown={true}>
          <TableCell className={classes.tableCell}>{checkpointType.description}</TableCell>
          <TableCell>{formatDate(checkpointType.created)}</TableCell>
          <TableCell>{formatDate(checkpointType.updated)}</TableCell>
        </Hidden>
        <TableCell>
          <CheckpointTypeActions
            displayName={checkpointType.name}
            onUpdate={handleDeleteCheckpointType}
            onDelete={handleDeleteCheckpointType}
            code={code}
          />
        </TableCell>
      </TableRow>
    );
  }
};

const mapStateToProps = (state: State) => ({
  checkpointTypes: state.checkpointTypes,
  preferences: state.preferences
});
export default connect(mapStateToProps)(CheckpointTypeList);
